/**
 * LocalStorage 工具库，提供类型安全和过期时间管理
 */
class LocalStorageUtil {
    /**
     * 设置存储项（支持过期时间）
     * @param key string 存储键
     * @param value 存储值
     * @param expires number 过期时间（毫秒），默认永久
     */
    static setItem(key:string, value:any, expires = undefined) {
        try {
            const item = {
                value,
                expires: expires ? Date.now() + expires : null
            };
            localStorage.setItem(key, JSON.stringify(item));
        } catch (error) {
            console.error(`Failed to set localStorage item '${key}':`, error);
            // 可添加降级策略（如内存存储）
        }
    }

    /**
     * 获取存储项
     * @param key string
     * @returns 存储值或 null（如果不存在或已过期）
     */
    static getItem(key:string) {
        try {
            const itemStr = localStorage.getItem(key);
            if (!itemStr) return null;

            const item = JSON.parse(itemStr);
            // 检查是否过期
            if (item.expires && Date.now() > item.expires) {
                localStorage.removeItem(key);
                return null;
            }

            return item.value;
        } catch (error) {
            console.error(`Failed to get localStorage item '${key}':`, error);
            return null;
        }
    }

    /**
     * 移除存储项
     * @param key string
     */
    static removeItem(key:string) {
        try {
            localStorage.removeItem(key);
        } catch (error) {
            console.error(`Failed to remove localStorage item '${key}':`, error);
        }
    }

    /**
     * 清空所有存储项
     */
    static clear() {
        try {
            localStorage.clear();
        } catch (error) {
            console.error('Failed to clear localStorage:', error);
        }
    }

    /**
     * 获取所有存储键
     * @returns Array
     */
    static getKeys():string[] {
        try {
            return Object.keys(localStorage);
        } catch (error) {
            console.error('Failed to get localStorage keys:', error);
            return [];
        }
    }

    /**
     * 检查存储项是否存在（不过期）
     * @param key 存储键
     * @returns boolean
     */
    static hasItem(key:string) {
        return this.getItem(key) !== null;
    }

    /**
     * 获取存储项数量
     * @returns number
     */
    static getLength() {
        try {
            return localStorage.length;
        } catch (error) {
            console.error('Failed to get localStorage length:', error);
            return 0;
        }
    }
}

export default LocalStorageUtil;